import * as constants from '../constants/CONSTANT';

const GenerateDocumentApi = {
  /**
   * Generate and download document (PDF) for an object
   * @param {string} objectName - Object name (e.g., 'quote', 'opportunity')
   * @param {string} recordId - Record ID
   * @param {string} action - Action type (e.g., 'customer quote'), defaults to 'customer quote'
   * @param {boolean} createAttachment - Whether to attach the generated document to the record
   * @param {{ returnForDisplay?: boolean }} options - If returnForDisplay is true, returns { blobUrl, filename } for in-chat view/download
   * @returns {Promise<void>} When options.returnForDisplay is false/undefined
   * @returns {Promise<{ blobUrl: string, filename: string }>} When options.returnForDisplay is true
   */
  async generateDocument(objectName, recordId, action = 'customer quote', createAttachment = false, options = {}) {
    const returnForDisplay = options && options.returnForDisplay === true;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = `${constants.API_BASE_URL}/api/generatedocument/${objectName}/${recordId}?action=${encodeURIComponent(action)}&createAttachment=${createAttachment}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token,
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate document';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          try {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          } catch (_) {}
        }
        throw new Error(errorMessage);
      }

      const contentDisposition = response.headers.get('Content-Disposition') || '';
      let filename = `${objectName}_document.pdf`;
      const starMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
      if (starMatch && starMatch[1]) {
        try {
          filename = decodeURIComponent(starMatch[1].trim());
        } catch (_) {}
      }
      if (filename === `${objectName}_document.pdf`) {
        const filenameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
        if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1].trim();
      }
      const safeDownloadName = filename.replace(/[^\w.-]/g, '_').replace(/_{2,}/g, '_') || 'document.pdf';
      if (!safeDownloadName.toLowerCase().endsWith('.pdf')) {
        filename = safeDownloadName + '.pdf';
      } else {
        filename = safeDownloadName;
      }

      const contentType = response.headers.get('Content-Type') || 'application/pdf';
      const arrayBuffer = await response.arrayBuffer();
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('Download failed: received empty file. The generated PDF may be empty.');
      }
      const blob = new Blob([arrayBuffer], { type: contentType.split(';')[0].trim() });
      const blobUrl = URL.createObjectURL(blob);

      if (returnForDisplay) {
        return { blobUrl, filename };
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  },

  /**
   * Generate quote document from a lead: creates a quote linked to the lead and downloads the PDF.
   * Use when the user is on a lead record and wants to generate a quote (they don't need to be on a quote record).
   * @param {string} leadId - Lead record ID
   * @param {string} action - Action type (e.g. 'customer quote'), defaults to 'customer quote'
   * @param {{ returnForDisplay?: boolean }} options - If returnForDisplay is true, returns { blobUrl, filename } for in-chat view/download instead of triggering download
   * @returns {Promise<void>} When options.returnForDisplay is false/undefined
   * @returns {Promise<{ blobUrl: string, filename: string }>} When options.returnForDisplay is true
   */
  async generateQuoteFromLead(leadId, action = 'customer quote', options = {}) {
    const returnForDisplay = options && options.returnForDisplay === true;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const url = `${constants.API_BASE_URL}/api/generatedocument/from-lead/${leadId}?action=${encodeURIComponent(action)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate quote from lead';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          try {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          } catch (_) { }
        }
        throw new Error(errorMessage);
      }

      const contentDisposition = response.headers.get('Content-Disposition') || '';
      let filename = `quote_from_lead_${leadId}.pdf`;
      const starMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
      if (starMatch?.[1]) {
        try {
          filename = decodeURIComponent(starMatch[1].trim());
        } catch (_) { }
      } else {
        const filenameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
        if (filenameMatch?.[1]) filename = filenameMatch[1].trim();
      }
      const safeDownloadName = filename.replace(/[^\w.-]/g, '_').replace(/_{2,}/g, '_') || 'quote.pdf';
      if (!safeDownloadName.toLowerCase().endsWith('.pdf')) {
        filename = safeDownloadName + '.pdf';
      } else {
        filename = safeDownloadName;
      }

      const contentType = response.headers.get('Content-Type') || 'application/pdf';
      const arrayBuffer = await response.arrayBuffer();
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('Download failed: received empty file.');
      }
      const blob = new Blob([arrayBuffer], { type: contentType.split(';')[0].trim() });
      const blobUrl = URL.createObjectURL(blob);

      if (returnForDisplay) {
        return { blobUrl, filename };
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error generating quote from lead:', error);
      throw error;
    }
  },
};

export default GenerateDocumentApi;
